'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useId, useRef, useState } from 'react';

import { GA_EVENTS, GA_PARAMS } from '@/constants/analytics';
import { trackEvent } from '@/lib/analytics/trackEvent';
import {
  localeDisplayNames,
  localeHreflang,
  localizedHref,
  locales,
  pathWithoutLocale,
  type Locale,
} from '@/lib/i18n';

import styles from './NavbarLanguageMenu.module.css';

type NavbarLanguageMenuTone = 'hero' | 'onBlue';

type NavbarLanguageMenuProps = {
  locale: Locale;
  /** Accessible name for the language menu trigger (from server messages). */
  languageSwitcherLabel: string;
  /** `onBlue` = white globe for the mobile menu actions block. */
  tone?: NavbarLanguageMenuTone;
  /** Globe-only trigger (Figma mobile language selection). */
  compact?: boolean;
};

export function NavbarLanguageMenu({
  locale: currentLocale,
  languageSwitcherLabel,
  tone = 'hero',
  compact = false,
}: NavbarLanguageMenuProps) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname() ?? '/';
  const path = pathWithoutLocale(pathname);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const rootClassName = [
    styles.root,
    tone === 'onBlue' ? styles.toneOnBlue : '',
    compact ? styles.compact : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.globe} aria-hidden>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
            <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5" />
            <ellipse cx="12" cy="12" rx="4.5" ry="9.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2.5 12h19" stroke="currentColor" strokeWidth="1.5" />
            <path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
        {compact ? null : (
          <span className={styles.chevron} aria-hidden>
            <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
              <path d="M2 4.5 6 8.5 10 4.5" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </span>
        )}
        <span className={styles.srOnly}>{languageSwitcherLabel}</span>
      </button>

      {open ? (
        <ul id={menuId} className={styles.menu} role="menu">
          {locales.map((locale) => (
            <li key={locale} role="none">
              <Link
                role="menuitem"
                href={localizedHref(locale, path)}
                hrefLang={localeHreflang(locale)}
                className={styles.menuItem}
                aria-current={locale === currentLocale ? 'page' : undefined}
                onClick={() => {
                  if (locale !== currentLocale) {
                    trackEvent(GA_EVENTS.languageSwitch, {
                      [GA_PARAMS.fromLocale]: currentLocale,
                      [GA_PARAMS.toLocale]: locale,
                    });
                  }
                  setOpen(false);
                }}
              >
                {localeDisplayNames[locale]}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
